<template>
<div>
    <vue-headful :title="(role_id ? 'Edit: '+role.name : 'New Role') + ' [Roles] :: MeritEHS'" />

    <h4 class="py-3 mb-4">
        <ol class="breadcrumb font-weight-bold m-0">
             <li class="breadcrumb-item font-weight-light">
                <router-link :to="`/users`">
                    <i class="fa fa-user"></i> Users
                </router-link>
            </li>
            <li class="breadcrumb-item font-weight-light">
                <router-link :to="`/users#roles`">
                    Settings - Roles
                </router-link>
            </li>
            <li class="breadcrumb-item active">
                <template v-if="role_id">Edit: {{ role.name }}</template>
                <template v-else>Create Role</template>
            </li>
        </ol>
    </h4>
    <template v-if="!done_loading">
        <i class="fa fa-circle-o-notch fa-spin"></i> Loading
    </template>
    <template v-else>
        <div v-if="role.id === 7 || role.slug === 'administrator'" class="alert alert-warning">
            <strong>Note:</strong> You cannot edit the administrator role. It is a special role that inherits ALL permissions.
        </div>
        <div class="row mb-4" v-else>
            <div class="col-lg-8 col-12 mb-4 mb-lg-0">
                <div class="card mb-4">
                    <div class="card-header">
                        <strong>Select Permissions:</strong>
                    </div>
                    <div class="card-body">
                        <b-row>
                            <b-col>
                                <div v-for="p in pcol1">
                                    <b-form-checkbox vbind:id="p.id" v-model="p['status']" unchecked-value=0 value=1 plain>
                                        <i class="fa fa-question-circle" :title="p['description']"></i> {{ p.name }}
                                    </b-form-checkbox>
                                </div>
                            </b-col>
                            <b-col>
                                <div v-for="p in pcol2">
                                    <b-form-checkbox vbind:id="p.id" v-model="p['status']" unchecked-value=0 value=1 plain>
                                        <i class="fa fa-question-circle" :title="p['description']"></i> {{ p.name }}
                                    </b-form-checkbox>
                                </div>
                            </b-col>
                            <b-col>
                                <div v-for="p in pcol3">
                                    <b-form-checkbox vbind:id="p.id" v-model="p['status']" unchecked-value=0 value=1 plain>
                                        <i class="fa fa-question-circle" :title="p['description']"></i> {{ p.name }}
                                    </b-form-checkbox>
                                </div>
                            </b-col>
                        </b-row>
                    </div>
                </div>

                <template v-if="role_id">
                    <div class="card mb-4">
                        <div class="card-header"><strong>Users With This Role ({{ user_count }})</strong></div>
                        <div class="card-body">
                            <user-list assetType="Role" :assetId="role.id" @userListCount="((count) => { this.user_count = count; })" />
                        </div>
                    </div>

                    <div class="card" v-if="false">
                        <div class="card-header">
                            <strong>Inspection Goals</strong> <router-link :to="`/profile/reminders/edit?role_id=${role.id}`" class="btn btn-sm btn-success ml-3" title="Create Role Inspection Goal"><i class="fa fa-plus-circle"></i></router-link>
                        </div>
                        <div class="card-body">
                            <div class="alert alert-info">
                                Setting inspection Goals on a Role will apply the goal to all users with this role.
                            </div>
                            <inspection-goal-list :role_id="role.id"></inspection-goal-list>
                        </div>
                    </div>
                </template>
            </div>
            <div class="col-lg-4 col-12">
                <div class="card mb-4">
                    <div class="card-header">
                        <strong>Role Settings:</strong>
                    </div>
                    <div class="card-body">
                        <div class="mb-2">
                            <label for="role-name" :class="{'text-danger': errors['role.name']}">
                                Role Name: <i class="fa fa-asterisk text-danger"></i>
                            </label>
                            <b-form-input id="role-name" v-model="role.name" type="text" placeholder="Enter Role Name" :class="{'is-invalid': errors['role.name']}" />

                            <span v-if="errors['role.name']" class="text-danger">
                               <span v-for="error in errors['role.name']">{{ error }}</span>
                            </span>
                        </div>
                        <div class="mb-2">
                            <label for="description">Description:</label>
                            <b-form-textarea id="description" v-model="role.description" :rows="3" :max-rows="6" placeholder="Enter a Description for this Role" />
                        </div>
                        <div>
                            <b-form-checkbox id="activated" v-model="role.is_active" unchecked-value=0 value=1 plain>
                                Active
                            </b-form-checkbox>
                        </div>
                    </div>
                    <div class="card-footer">
                        <b-button variant="success" v-on:click="postPermissions" :disabled="saving">
                            <template v-if="saving">
                                <i class="fa fa-spinner fa-pulse"></i> Saving
                            </template>
                            <template v-else>
                                Save Role
                            </template>
                        </b-button>
                    </div>
                </div>

                <div class="card mb-4">
                    <div class="card-header">
                        <strong>Auto-Assigned Titles ({{ titles.length }})</strong>
                        <b-button size="sm" variant="success" v-on:click="titles.push('')" :disabled="saving"><i class="fa fa-plus-circle"></i></b-button>
                    </div>
                    <div class="card-body">
                        <table class="table table-bordered">
                            <thead>
                                <tr>
                                    <th>Actions</th>
                                    <th>Title</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr :key="`title-row-${title}`" v-for="(title,index) in titles">
                                    <td><b-button size="sm" variant="danger" @click="titles.splice(index, 1)">Remove</b-button></td>
                                    <td><inline-edit :key="`title-edit-${title}`" :value="title" @input="titles[index] = $event" :startEditing="!title" :size="sm" :placeholder="'Enter Title Name'" /></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="card mb-4">
                    <div class="card-header">
                        <strong><i class="fa fa-question-circle" title="When a User is Assigned this Role, MeritEHS will sync the following list of dashboards and subscribe the user."></i> Auto-Subscribed Dashboards ({{ dashboards.length }})</strong>
                        <b-button size="sm" variant="success" v-on:click="show_add_dashboard = true" :disabled="saving"><i class="fa fa-plus-circle"></i></b-button>
                    </div>
                    <div class="card-body">
                        <table class="table table-bordered">
                            <thead>
                                <tr>
                                    <th>Actions</th>
                                    <th>Title</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(dash, i) in dashboards">
                                    <td>
                                        <button type="button" class="btn btn-xs btn-default" @click="dashboards.splice(i, 1)">
                                            <i class="fa fa-times text-danger"></i>
                                        </button>
                                    </td>
                                    <td>{{ dash.dashboard_name }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="alert alert-danger mt-3" v-if="errors.role_billing_error">
                <h4 class="text-danger">User Licenses Exceeded</h4>
                <div class="mb-1" v-for="error in errors.role_billing_error">{{ error }}</div>
                <p>To upgrade your user licenses, an admin can visit the <router-link to="/billing">Account Billing Settings</router-link></p>
            </div>
        </div>
    </template>

    <b-modal
        v-model="show_add_dashboard"
        title="Add Dashboard"
        ok-title="Add Dashboard"
        ok-variant="success"
        no-fade
        @hide="handleHide"
        @hidden="handleHidden"
        @ok="handleOk"
        ref="roleDashboardAdd"
        :no-close-on-backdrop="true"
        scrollable>

        <div class="">
            <label class="text-muted">Search Global Dashboards:</label>

            <b-form-select v-model="add_dashboard" :options="dashboardOptions" />
        </div>
    </b-modal>
</div>
</template>

<script>
export default {
    data() {
        return {
            done_loading: false,
            show_add_dashboard: false,
            saving: false,
            role: {},
            titles: [],
            permissions: [],
            dashboards: [],
            global_dashboards: [],
            role_id: this.$route.params.id,
            count_per_column: 0,
            user_count: null,
            add_dashboard: null,
            pcol1: [],
            pcol2: [],
            pcol3: [],
            errors: {},
        }
    },

    computed: {
        dashboardOptions(){
            //Setup options
            var options = [
                {value: null, text: '-- Select Dashboard --'},
            ];

            //Loop through and build lists
            for(var i = 0; i < this.global_dashboards.length; i++)
            {
                //Push onto options
                options.push({value: this.global_dashboards[i].id, text: this.global_dashboards[i].dashboard_name});
            }

            //Send it
            return options;
        },

    },

    methods: {
        getRolesAndPermissions() {
            console.log('test');

            //Get the data
            this.$http
                .get('admin/role-edit' + (this.role_id ? '/' + this.role_id : ''))
                .then(response => {
                    //TEtsing
                    //console.log(response);

                    //Set all the data
                    this.role = response.data.role;
                    this.permissions = response.data.permissions;

                    //Set Titelse
                    if(typeof this.role.titles != 'undefined')
                    {
                        this.titles = this.role.titles;
                    }

                    //Set Other Data
                    this.count_per_column = Math.ceil(this.permissions.length / 3);
                    this.dashboards = response.data.dashboards;
                    this.global_dashboards = response.data.global_dashboards;

                    //Build the Columns
                    if (this.count_per_column >= 4) {
                        var ix = 0;
                        for (var x = 0; x < this.count_per_column; ++x) {
                            this.pcol1.push(this.permissions[ix]);
                            ix++;
                        }
                        for (var x = 0; x < this.count_per_column; ++x) {
                            this.pcol2.push(this.permissions[ix]);
                            ix++;
                        }
                        for (var x = 0; x < this.count_per_column; ++x) {
                            if (this.permissions[ix]) {
                                this.pcol3.push(this.permissions[ix]);
                                ix++;
                            }
                        }
                    }
                })
                .catch(response => {
                    //How to handle the error
                    console.log(response);
                })
                .then(() => {
                    //Set loading
                    this.done_loading = true;
                });
        },

        postPermissions() {
            //Saving
            this.saving = true;

            //Set the Role Tites
            this.role.titles = this.titles;

            var retval = [];

            this.pcol1.forEach((p) => {
                if (p['status'] == 1) {
                    retval.push(p['id']);
                }
            });
            this.pcol2.forEach((p) => {
                if (p['status'] == 1) {
                    retval.push(p['id']);
                }
            });
            this.pcol3.forEach((p) => {
                if (p['status'] == 1) {
                    retval.push(p['id']);
                }
            });

            this.$http
                .post('admin/role-edit' + (this.role_id ? '/' + this.role_id : ''), {
                    'role': this.role,
                    'permissions': retval,
                    'dashboard_ids': this.dashboards.map(d => d.id),
                })
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Role Saved');

                    //Return to main list?
                    if(!this.role_id)
                    {
                        this.$router.push('/users/roles/edit/'+response.data.id);
                    }
                })
                .catch(error => {
                    //Check for error
                    if (error.response.status === 422) {
                        //Display validation errors
                        this.errors = error.response.data
                    }
                })
                .then(response => {
                    //SHow loading
                    this.saving = false;
                });
        },

        cancelHandler(){
            //Return to main list
            this.$router.push('/admin/roles');
        },

        handleHide(bvEvt) {

        },

        handleHidden(bvEvt) {
            //Reset the values
            this.show_add_dashboard = false;
            this.add_dashboard = null;
        },

        handleOk(bvEvt) {
            //Stops the modal from closing
            bvEvt.preventDefault();

            //Check Dashboard
            if(!this.add_dashboard)
            {
                //Alert the user
                this.$toastr.warning('Select a Dashboard');
            }

            //Push onto the Stack
            for (var i = 0; i < this.global_dashboards.length; i++)
            {
                if(this.global_dashboards[i].id === this.add_dashboard)
                {
                    //Pusht it
                    this.dashboards.push(this.global_dashboards[i]);
                }
            }

            //Hide and Reset
            this.show_add_dashboard = false;
            this.add_dashboard = null;
        },
    },

    mounted() {
        //Get the injuries
        this.getRolesAndPermissions();
    }
}
</script>
